import { Currency } from "@redotech/money/currencies";
import { moneyFormat } from "@redotech/money/util";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left_filled.svg";
import ArrowRightIcon from "@redotech/redo-icons/arrow-right_filled.svg";
import CheckIcon from "@redotech/redo-icons/check.svg";
import {
  ProductDisplayInfoType,
  ProductDisplayMessageType,
} from "@redotech/redo-model/conversation/message-parsers/product-display-message-parser";
import { getShopifyProductDetailPageUrl } from "@redotech/redo-model/product";
import { GidType, toGid } from "@redotech/redo-model/shopify/gid-utils";
import { getProduct } from "@redotech/shopify-storefront";
import { ShopifyStorefrontClient } from "@redotech/shopify-storefront/storefront-client";
import classNames from "classnames";
import "keen-slider/keen-slider.min.css";
import { useKeenSlider } from "keen-slider/react";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RedoButton } from "../arbiter-components/buttons/redo-button";
import { RedoListItem } from "../arbiter-components/list/redo-list";
import { RedoSpinner } from "../arbiter-components/loading/redo-spinner";
import { RedoSingleSelectDropdownInput } from "../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "../flex";
import { Text } from "../text";
import * as productDisplayCarouselCss from "./product-display-carousel.module.css";

type HandleAddToCartFn = ({
  productId,
  variantId,
}: {
  productId: string;
  variantId: string;
}) => Promise<void>;

type ProductIdType = { productId: string };

export const ProductDisplayCarousel = memo(function ProductDisplayCarousel({
  message,
  storeUrl,
  readOnly,
  storefrontAccessToken,
  handleAddToCart,
}: {
  message: ProductDisplayMessageType;
  storeUrl?: string;
  readOnly: boolean;
  storefrontAccessToken?: string;
  handleAddToCart?: HandleAddToCartFn;
}) {
  const componentRef = useRef(null);

  const productIds = useMemo(() => {
    return (
      message.structuredContent?.productsToDisplay?.map(
        (item: ProductIdType) => item.productId,
      ) || []
    );
  }, [message.structuredContent?.productsToDisplay]);

  const productsResult = useLoad(async () => {
    if (!productIds.length || !storeUrl || !storefrontAccessToken) return [];

    try {
      const client = new ShopifyStorefrontClient(
        storeUrl,
        storefrontAccessToken,
      );

      const productDetailsPromises = productIds.map(async (productId) => {
        try {
          const formattedId = toGid(GidType.PRODUCT, productId);

          const product = await getProduct(client, formattedId);

          if (!product) {
            throw new Error(`Product with ID ${productId} not found`);
          }

          return {
            id: productId,
            handle: product.handle || productId,
            title: product.title,
            image: product.featuredImage
              ? {
                  src: product.featuredImage.url,
                  alt: product.featuredImage.altText || product.title,
                }
              : undefined,
            priceRange: {
              minPrice: {
                amount: product.priceRange.minVariantPrice.amount,
                currency: product.priceRange.minVariantPrice.currencyCode,
              },
              maxPrice: {
                amount: product.priceRange.maxVariantPrice.amount,
                currency: product.priceRange.maxVariantPrice.currencyCode,
              },
            },
            variants: product.variants.nodes.map(
              (variant: { id: string; title: any }) => ({
                id: variant.id.split("/").pop() || variant.id,
                title:
                  variant.title === "Default Title"
                    ? product.title
                    : variant.title,
              }),
            ),
          };
        } catch (error) {
          console.error(`Error fetching product ${productId}:`, error);
          return null;
        }
      });

      const productResults = await Promise.all(productDetailsPromises);
      return productResults.filter(
        (product): product is NonNullable<typeof product> => product !== null,
      );
    } catch (error) {
      console.error("Error fetching products:", error);
      throw error;
    }
  }, [productIds, storeUrl, storefrontAccessToken]);

  if (productsResult.pending) {
    return (
      <Flex
        className={classNames(
          productDisplayCarouselCss.productDisplayCarouselContainer,
        )}
        justify="flex-start"
        position="relative"
        ref={componentRef}
      >
        <Carousel>
          {productIds.map((_, index) => (
            <ProductLoadingTile key={index} />
          ))}
        </Carousel>
      </Flex>
    );
  }

  if (productsResult.error || !productsResult.value) {
    return (
      <Flex justify="center" p="lg">
        <Text color="error">Failed to load product details</Text>
      </Flex>
    );
  }

  return (
    <Flex
      className={classNames(
        productDisplayCarouselCss.productDisplayCarouselContainer,
      )}
      justify="flex-start"
      position="relative"
      ref={componentRef}
    >
      <Carousel>
        {productsResult.value.map((product, index) => (
          <ProductTile
            handleAddToCart={handleAddToCart}
            key={index}
            product={product}
            readOnly={readOnly}
            storeUrl={storeUrl}
          />
        ))}
      </Carousel>
    </Flex>
  );
});

const ProductLoadingTile = memo(function ProductLoadingTile() {
  return (
    <Flex
      className={productDisplayCarouselCss.productTileContainer}
      dir="column"
      gap="none"
      radius="sm"
    >
      <Flex
        align="center"
        className={productDisplayCarouselCss.productTileImageContainer}
        justify="center"
      >
        <RedoSpinner size="md" />
      </Flex>
    </Flex>
  );
});

export const Carousel = memo(function Carousel({
  children,
}: {
  children: React.ReactNode[];
}) {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [details, setDetails] = useState<any>(null);

  const [sliderRef, instanceRef] = useKeenSlider(
    {
      selector: ".keen-slider__slide",
      loop: false,
      mode: "snap",
      slides: { origin: "center", perView: 1.75, spacing: 15 },
      slideChanged(slider) {
        setCurrentSlide(slider.track.details.rel);
      },
      detailsChanged(slider) {
        setDetails(slider.track.details);
      },
      created(slider) {
        // Center the initial slide
        slider.moveToIdx(0, true);
      },
    },
    [],
  );

  function scaleStyle(idx: number) {
    if (!details) return {};
    const slide = details.slides[idx];
    const scale_size = 0.1;
    const opacity_size = 0.8;
    const scale = 1 - (scale_size - scale_size * slide.portion);
    const opacity = 1 - (opacity_size - opacity_size * slide.portion);
    return {
      transform: `scale(${scale})`,
      WebkitTransform: `scale(${scale})`,
      opacity,
    };
  }

  const shouldShowRightArrow = useMemo(() => {
    return currentSlide < children.length - 1;
  }, [currentSlide, children.length]);

  const shouldShowLeftArrow = useMemo(() => {
    return currentSlide > 0;
  }, [currentSlide]);

  const getSlideClass = useCallback(
    (index: number) => {
      if (index === currentSlide) {
        return productDisplayCarouselCss.currentSlide;
      } else if (Math.abs(index - currentSlide) === 1) {
        return productDisplayCarouselCss.adjacentSlide;
      } else {
        return productDisplayCarouselCss.otherSlide;
      }
    },
    [currentSlide],
  );

  return (
    <>
      <div
        className={classNames(
          "keen-slider",
          children.length === 1 && productDisplayCarouselCss.sliderOverflow,
        )}
        ref={sliderRef}
      >
        {children.map((child, index) => (
          <div
            className={classNames(
              "keen-slider__slide",
              productDisplayCarouselCss.carouselElementContainer,
              productDisplayCarouselCss.sliderOverflow,
              getSlideClass(index),
            )}
            key={index}
          >
            <Flex style={scaleStyle(index)} w="full">
              {child}
            </Flex>
          </div>
        ))}
      </div>
      {shouldShowLeftArrow && (
        <div
          className={classNames(
            productDisplayCarouselCss.carouselArrowButton,
            productDisplayCarouselCss.left,
          )}
        >
          <ArrowButton
            ArrowIcon={ArrowLeftIcon}
            onClick={() => instanceRef.current?.prev()}
          />
        </div>
      )}
      {shouldShowRightArrow && (
        <div
          className={classNames(
            productDisplayCarouselCss.carouselArrowButton,
            productDisplayCarouselCss.right,
          )}
        >
          <ArrowButton
            ArrowIcon={ArrowRightIcon}
            onClick={() => instanceRef.current?.next()}
          />
        </div>
      )}
    </>
  );
});

const ProductTile = memo(function ProductTile({
  product,
  storeUrl,
  readOnly,
  handleAddToCart,
}: {
  product: ProductDisplayInfoType;
  storeUrl: string | undefined;
  readOnly: boolean;
  handleAddToCart: HandleAddToCartFn | undefined;
}) {
  const handleClick = useHandler(() => {
    if (!readOnly && storeUrl) {
      window.open(
        getShopifyProductDetailPageUrl(storeUrl, product.handle),
        "_self",
      );
    }
  });

  const priceRangeString = useMemo(() => {
    if (product.priceRange) {
      const minPrice = moneyFormat({
        amount: +product.priceRange.minPrice.amount,
        currency: product.priceRange.minPrice.currency as string as Currency,
      });
      const maxPrice = moneyFormat({
        amount: +product.priceRange.maxPrice.amount,
        currency: product.priceRange.maxPrice.currency as string as Currency,
      });
      if (minPrice === maxPrice) {
        return minPrice;
      } else {
        return `${minPrice} - ${maxPrice}`;
      }
    }
    return undefined;
  }, [product.priceRange]);

  const variantOptions: RedoListItem<{ id: string; title: string }>[] =
    useMemo(() => {
      return product.variants.map((variant) => ({
        id: variant.id,
        value: variant,
        type: "text",
        text: variant.title,
      }));
    }, [product.variants]);

  const [selectedOption, setSelectedOption] = useState<
    RedoListItem<{ id: string; title: string }> | undefined
  >(variantOptions[0]);

  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showAddedToCartSuccess, setShowAddedToCartSuccess] = useState(false);

  useEffect(() => {
    if (showAddedToCartSuccess) {
      setTimeout(() => {
        setShowAddedToCartSuccess(false);
      }, 5000);
    }
  }, [showAddedToCartSuccess]);

  const handleAddToCartClick = useCallback(
    async (event: React.MouseEvent) => {
      event.stopPropagation();
      if (handleAddToCart && selectedOption?.value.id) {
        try {
          setIsAddingToCart(true);
          await handleAddToCart({
            productId: product.id,
            variantId: selectedOption.value.id,
          });
          setShowAddedToCartSuccess(true);
        } catch (error) {
          console.error(error);
        } finally {
          setIsAddingToCart(false);
        }
      }
    },
    [product.id, selectedOption, handleAddToCart],
  );

  return (
    <Flex
      as={readOnly ? "div" : "button"}
      className={productDisplayCarouselCss.productTileContainer}
      dir="column"
      gap="lg"
      radius="sm"
    >
      <Flex dir="column" gap="lg">
        <Flex
          className={productDisplayCarouselCss.productTileImageContainer}
          onClick={handleClick}
        >
          <img
            alt={product.image?.alt}
            className={productDisplayCarouselCss.productTileImage}
            src={product.image?.src}
          />
        </Flex>
        <Text
          className={productDisplayCarouselCss.productTileTitle}
          color="primary"
          fontSize="sm"
          fontWeight="semibold"
          px="xl"
        >
          {product.title}
        </Text>
        <Text color="secondary" fontSize="xs" fontWeight="regular" px="xl">
          {priceRangeString}
        </Text>
      </Flex>
      {!!product.variants.length && (
        <Flex dir="column" gap="md" pb="md" pt="sm" px="xl">
          <RedoSingleSelectDropdownInput
            options={variantOptions}
            optionSelected={({ item }) => {
              setSelectedOption(item);
            }}
            placeholder="Select variant"
            selectedItem={selectedOption}
          />
          <Flex pb="md">
            <RedoButton
              className={productDisplayCarouselCss.addToCartButton}
              disabled={readOnly || isAddingToCart}
              hierarchy="primary"
              IconLeading={showAddedToCartSuccess ? CheckIcon : undefined}
              onClick={handleAddToCartClick}
              pending={isAddingToCart}
              size="sm"
              text={showAddedToCartSuccess ? "Added" : "Add to cart"}
              theme="normal"
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
});

const ArrowButton = memo(function ArrowButton({
  ArrowIcon,
  onClick,
}: {
  ArrowIcon: React.JSXElementConstructor<any>;
  onClick: () => void;
}) {
  const handleClick = useHandler((event: React.MouseEvent) => {
    event.stopPropagation();
    onClick();
  });

  return (
    <Flex
      align="center"
      as="button"
      className={productDisplayCarouselCss.arrowButtonContainer}
      justify="center"
      onClick={handleClick}
      radius="full"
    >
      <ArrowIcon className={productDisplayCarouselCss.arrowButtonIcon} />
    </Flex>
  );
});
